import { TriangleAlert } from "lucide-react";
import { useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { type AppRegistrationRequestDetail, useRejectAppRegistration } from "@/lib/api";
import { readApiErrorMessage } from "@/lib/api-errors";
import { logger } from "@/lib/logger";

interface RejectDialogProps {
  request: AppRegistrationRequestDetail | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function RejectDialog({ request, open, onOpenChange }: RejectDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        {request && open && (
          // Remount per request so the note never carries into another decision.
          <RejectForm key={request.id} request={request} onClose={() => onOpenChange(false)} />
        )}
      </DialogContent>
    </Dialog>
  );
}

function RejectForm({
  request,
  onClose,
}: {
  request: AppRegistrationRequestDetail;
  onClose: () => void;
}) {
  const reject = useRejectAppRegistration();
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const pending = reject.isPending;
  // A sibling row that failed is the one rejection that can leave Entra ahead
  // of the record: an amendment applies its change in a single call, so a
  // response that timed out — or a commit that failed after it — marks the row
  // failed while the application already holds the new permissions. Approving
  // again is idempotent and reconciles the two; rejecting ends the chain with
  // the desk recording the old set, and every later change is then refused as
  // drift until an admin fixes the application by hand.
  // An amendment only: a decommission that failed before deleting anything
  // changed nothing in Entra (one that deleted something is not rejectable).
  const mayAlreadyBeApplied = request.request_type === "amend" && request.status === "failed";
  const isDecommission = request.request_type === "delete";
  // The backend requires a non-empty note (min_length=1); mirror that here so
  // the approver is told before the round trip, not by a 422 afterwards.
  const canSubmit = note.trim().length > 0 && !pending;

  const handleReject = async () => {
    if (!canSubmit) {
      return;
    }
    setError(null);
    try {
      await reject.mutateAsync({ requestId: request.id, note: note.trim() });
      onClose();
    } catch (err) {
      logger.error("[app-registrations] reject failed:", err);
      setError(readApiErrorMessage(err, "Failed to reject the request. Please try again."));
    }
  };

  return (
    <>
      <DialogHeader>
        <DialogTitle>
          {isDecommission ? "Reject decommission request" : "Reject app registration request"}
        </DialogTitle>
        <DialogDescription>
          <strong>{request.display_name}</strong> for {request.project_name}, requested by{" "}
          {request.requester_email}.{" "}
          {isDecommission
            ? "Rejecting keeps the registration as it is in Entra — nothing has been deleted — and the requester can ask again later."
            : "Rejecting is final — there is nothing to retry, and the requester has to raise a new request."}
        </DialogDescription>
      </DialogHeader>

      <DialogBody>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="reject-note">Reason (required)</Label>
            <Textarea
              id="reject-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="What would need to change for this to be approved?"
              rows={4}
              disabled={pending}
            />
            <p className="text-muted-foreground text-xs">
              The requester reads this. &ldquo;No&rdquo; without a reason is not actionable.
            </p>
          </div>

          {mayAlreadyBeApplied && (
            <div className="rounded-md border border-amber-300 bg-amber-50 p-3 dark:border-amber-900 dark:bg-amber-950/40">
              <p className="flex items-start gap-2 text-sm font-medium text-amber-900 dark:text-amber-200">
                <TriangleAlert className="mt-0.5 size-4 shrink-0" />
                This permission change may already be live in Entra
              </p>
              <p className="mt-1 text-xs text-amber-800 dark:text-amber-300">
                It is recorded as failed, but the change is applied in one call &mdash; so it may
                have landed and only the record of it failed.{" "}
                <strong>Approving it again is the only action that reconciles the two</strong>, and
                it is safe: nothing is created or changed twice. Reject only if you are sure the
                permissions should not be applied &mdash; after this, Scaffold UI will report the
                old set, and no further change to this registration can be approved until a
                directory admin restores it in the portal.
              </p>
            </div>
          )}

          {error && (
            <p className="text-destructive flex items-start gap-2 text-sm">
              <TriangleAlert className="mt-0.5 size-4 shrink-0" />
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="mt-6">
          <Button variant="outline" onClick={onClose} disabled={pending}>
            Cancel
          </Button>
          <Button variant="destructive" onClick={handleReject} disabled={!canSubmit}>
            {pending ? "Rejecting…" : "Reject request"}
          </Button>
        </DialogFooter>
      </DialogBody>
    </>
  );
}
